import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { usePageCms } from "@/lib/usePageCms";
import { PageHero } from "@/components/site/PageHero";
import { ListingIntro } from "@/components/site/ListingIntro";
import { ListingEmpty } from "@/components/site/ListingEmpty";
import { ListingPagination } from "@/components/site/ListingPagination";
import { sortItems } from "@/lib/listing";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Clock, MapPin, X } from "lucide-react";

export default function Itineraries() {
  const FB = { overline: "Sample Programmes", title: "Sample Itinerary Library", subtitle: "Search and filter ready reference itineraries. Every journey is fully customizable for your clients.", image: "https://images.unsplash.com/photo-1712186870325-00427d06341a?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000", seo_title: "Sample Maharashtra Itineraries for Travel Agents | Maha Sampada", seo_description: "Browse and filter sample Maharashtra itineraries — Konkan, wildlife, pilgrimage, heritage and hill-station journeys. Fully customizable, no fixed prices." };
  const { hero, sections } = usePageCms("itineraries", FB);
  const cfg = ((sections || []).find((s) => s.kind === "listing_config")) || { show_search: true, show_filters: true, filters: ["regions", "destinations", "theme", "traveller", "duration"], per_page: 24, empty_title: "No itineraries match your filters", empty_body: "Try adjusting or clearing your filters." };
  const fon = (f) => (cfg.filters || []).includes(f);
  const perPage = cfg.per_page || 24;
  const isList = (cfg.layout || "grid") === "list";
  useSeo({ title: hero.seo_title || FB.seo_title, description: hero.seo_description || FB.seo_description });
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [region, setRegion] = useState("all");
  const [place, setPlace] = useState("all");
  const [theme, setTheme] = useState("all");
  const [traveller, setTraveller] = useState("all");
  const [nights, setNights] = useState("all");
  const [page, setPage] = useState(1);
  const resultsRef = useRef(null);

  useEffect(() => { api.get("/itineraries").then((r) => setAll(r.data)); }, []);

  const regions = useMemo(() => [...new Set(all.map((i) => i.region_name).filter(Boolean))], [all]);
  const themes = useMemo(() => [...new Set(all.map((i) => i.theme).filter(Boolean))], [all]);
  const travellers = useMemo(() => [...new Set(all.map((i) => i.traveller_type).filter(Boolean))], [all]);
  // Destination/Place options derived from existing itinerary→place relationships (stable place_id, never renamed labels).
  const places = useMemo(() => {
    const map = new Map();
    all.forEach((i) => (i.destinations || []).forEach((d) => { if (d && d.place_id && d.name && !map.has(d.place_id)) map.set(d.place_id, d.name); }));
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [all]);

  const filtered = all.filter((it) => {
    if (q && !`${it.title} ${(it.destinations || []).map((d) => d.name || d).join(" ")} ${it.snapshot}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (region !== "all" && it.region_name !== region) return false;
    if (place !== "all" && !(it.destinations || []).some((d) => d && d.place_id === place)) return false;
    if (theme !== "all" && it.theme !== theme) return false;
    if (traveller !== "all" && it.traveller_type !== traveller) return false;
    if (nights !== "all") {
      const n = it.nights || 0;
      if (nights === "short" && n > 3) return false;
      if (nights === "mid" && (n < 4 || n > 5)) return false;
      if (nights === "long" && n < 6) return false;
    }
    return true;
  });
  // Order: filter → sort → paginate.
  const sorted = sortItems(filtered, cfg.sort || "default", "title");
  const total = sorted.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const safePage = Math.min(page, totalPages);
  const pageItems = sorted.slice((safePage - 1) * perPage, safePage * perPage);
  const rangeStart = total === 0 ? 0 : (safePage - 1) * perPage + 1;
  const rangeEnd = Math.min(safePage * perPage, total);

  // Reset to page 1 whenever the result set or ordering changes.
  useEffect(() => { setPage(1); }, [q, region, place, theme, traveller, nights, cfg.sort, perPage]);

  const reset = () => { setQ(""); setRegion("all"); setPlace("all"); setTheme("all"); setTraveller("all"); setNights("all"); setPage(1); };
  const goPage = (p) => { setPage(p); resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const teaser = (html) => { const el = typeof document !== "undefined" ? document.createElement("div") : null; if (!el) return (html || "").replace(/<[^>]*>/g, ""); el.innerHTML = html || ""; return el.textContent || ""; };

  const Card = ({ it }) => (
    <div data-testid={`itinerary-result-${it.slug}`} className={`hover-lift overflow-hidden rounded-2xl border border-gold/25 bg-white ${isList ? "flex flex-col sm:flex-row" : "flex flex-col"}`}>
      <div className={`img-zoom relative overflow-hidden ${isList ? "h-48 sm:h-44 sm:w-64 sm:shrink-0 sm:self-start" : "h-48"}`}>
        <img src={it.cover_image} alt={it.title} className="h-full w-full object-cover" />
        {it.featured && <span className="absolute left-3 top-3 rounded-full bg-saffron px-3 py-1 font-accent text-[10px] font-semibold uppercase tracking-wide text-white">Featured</span>}
      </div>
      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-center gap-3 font-accent text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{it.duration}</span>
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{it.region_name}</span>
        </div>
        <h3 className="mt-2 font-display text-xl font-semibold leading-tight text-charcoal">{it.title}</h3>
        <span className="mt-1 w-fit rounded-full bg-forest/10 px-2 py-0.5 font-accent text-[10px] text-forest">{it.theme}</span>
        <p className={`mt-2 flex-1 text-sm text-muted-foreground ${isList ? "line-clamp-2" : "line-clamp-3"}`}>{teaser(it.snapshot)}</p>
        <div className={`mt-4 gap-2 ${isList ? "flex flex-wrap" : "flex flex-col"}`}>
          <Link to={`/itineraries/${it.slug}`}><Button size="sm" variant="outline" data-testid={`view-itinerary-${it.slug}`} className="w-full rounded-full border-charcoal font-accent text-xs hover:bg-charcoal hover:text-white">View Itinerary</Button></Link>
          <Link to={`/enquiry?ref=${encodeURIComponent(it.title)}`}><Button size="sm" data-testid={`quote-itinerary-${it.slug}`} className="w-full rounded-full bg-saffron font-accent text-xs text-white hover:bg-saffron-dark">Request B2B Quote</Button></Link>
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <PageHero overline={hero.overline} title={hero.title} subtitle={hero.subtitle}
        image={hero.image} crumbs={[{ label: "Sample Itineraries" }]} />

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        <ListingIntro cfg={cfg} />
        {/* Filters */}
        <div className="rounded-2xl border border-gold/25 bg-white p-5" style={{ display: (cfg.show_search || cfg.show_filters) ? undefined : "none" }}>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-6">
            {cfg.show_search && <div className="relative lg:col-span-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="itinerary-search" placeholder="Search journeys…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
            </div>}
            {cfg.show_filters && fon("regions") && <Select value={region} onValueChange={setRegion}><SelectTrigger data-testid="filter-region"><SelectValue placeholder="Region" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Regions</SelectItem>{regions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select>}
            {cfg.show_filters && fon("destinations") && <Select value={place} onValueChange={setPlace}><SelectTrigger data-testid="filter-destinations"><SelectValue placeholder="Destination" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Destinations</SelectItem>{places.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>}
            {cfg.show_filters && fon("theme") && <Select value={theme} onValueChange={setTheme}><SelectTrigger data-testid="filter-theme"><SelectValue placeholder="Theme" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Themes</SelectItem>{themes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>}
            {cfg.show_filters && fon("traveller") && <Select value={traveller} onValueChange={setTraveller}><SelectTrigger data-testid="filter-traveller"><SelectValue placeholder="Traveller" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Travellers</SelectItem>{travellers.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>}
            {cfg.show_filters && fon("duration") && <Select value={nights} onValueChange={setNights}><SelectTrigger data-testid="filter-duration"><SelectValue placeholder="Duration" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Any Duration</SelectItem><SelectItem value="short">Up to 3 nights</SelectItem><SelectItem value="mid">4–5 nights</SelectItem><SelectItem value="long">6+ nights</SelectItem></SelectContent></Select>}
          </div>
          <div className="mt-3 flex items-center justify-end">
            <button onClick={reset} data-testid="reset-filters" className="flex items-center gap-1 font-accent text-xs text-saffron hover:underline"><X className="h-3 w-3" />Reset filters</button>
          </div>
        </div>

        {/* Always-visible results summary (survives hidden search/filters) */}
        <div className="mt-4">
          <span className="font-accent text-xs text-muted-foreground" data-testid="itinerary-count">{total > perPage ? `Showing ${rangeStart}–${rangeEnd} of ${total} itineraries` : `${total} ${total === 1 ? "itinerary" : "itineraries"}`}</span>
        </div>

        {/* Results */}
        {total === 0 ? (
          <ListingEmpty cfg={cfg} defaultTitle="No itineraries match your filters" defaultBody="Try adjusting or clearing your filters." onReset={reset} testid="itinerary-empty" />
        ) : (
          <div ref={resultsRef} style={{ scrollMarginTop: 100 }}>
            <div className={`mt-4 grid gap-6 ${isList ? "grid-cols-1" : "sm:grid-cols-2 lg:grid-cols-3"}`}>
              {pageItems.map((it) => <Card key={it.slug} it={it} />)}
            </div>
            <ListingPagination page={safePage} totalPages={totalPages} onChange={goPage} testid="itinerary-pagination" />
          </div>
        )}
      </section>
    </div>
  );
}
